import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  CheckCircle2,
  AlertTriangle,
  Camera,
  Keyboard,
  Package,
  ShieldAlert,
} from 'lucide-react';
import { Button } from './Button';
import { SalesOrderItem } from '../types/salesOrder';
import { BarcodeScannerCamera } from './BarcodeScannerCamera';

// ─── Types ───────────────────────────────────────────────────────

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  orderId: number;
  orderNumErp: number;
  items: SalesOrderItem[];
}

interface CheckoutItem extends SalesOrderItem {
  verifiedCount: number;
  isExempt: boolean; // is_checkout_required === false
}

type AlertType = 'not-in-order' | 'quantity-exceeded' | 'fraud';

interface AlertState {
  type: AlertType;
  message: string;
  sku?: string;
}

// ─── Sound Helpers (Web Audio API) ───────────────────────────────

const audioCtxRef = { current: null as AudioContext | null };

function getAudioCtx(): AudioContext {
  if (!audioCtxRef.current) {
    audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioCtxRef.current;
}

function playBeep() {
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.value = 1200;
    gain.gain.value = 0.3;
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
    osc.stop(ctx.currentTime + 0.15);
  } catch { /* silent fallback */ }
}

function playError() {
  try {
    const ctx = getAudioCtx();
    // Two short low buzzes
    [0, 0.15].forEach(delay => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'square';
      osc.frequency.value = 300;
      gain.gain.value = 0.3;
      osc.start(ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.12);
      osc.stop(ctx.currentTime + delay + 0.12);
    });
  } catch { /* silent fallback */ }
}

// ─── Scanner Detection ──────────────────────────────────────────
// USB barcode scanners typically type characters very fast (<50ms between keystrokes)
// and finish with Enter. We track keystroke timing to distinguish from manual typing.

const SCANNER_MIN_LENGTH = 3;

const normalizeCheckoutSku = (value: string) => value.trim().toUpperCase();

// ─── Component ──────────────────────────────────────────────────

export const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  orderNumErp,
  items,
}) => {
  // ─── State ──────────────────────────────────────────────────
  const [checkoutItems, setCheckoutItems] = useState<CheckoutItem[]>([]);
  const [scanInput, setScanInput] = useState('');
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [cameraMode, setCameraMode] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [pendingCameraScan, setPendingCameraScan] = useState<{ sku: string; item: CheckoutItem } | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  // Scanner detection refs
  const keystrokeTimestamps = useRef<number[]>([]);
  const scanBuffer = useRef('');

  // Camera mode ref (avoids stale closure in processSku)
  const cameraModeRef = useRef(cameraMode);
  useEffect(() => { cameraModeRef.current = cameraMode; }, [cameraMode]);

  // Scan lock ref: prevents duplicate camera reads while confirmation is open
  const scanLockRef = useRef(false);

  // Items ref (avoids stale closure in processSku)
  const checkoutItemsRef = useRef<CheckoutItem[]>([]);
  useEffect(() => { checkoutItemsRef.current = checkoutItems; }, [checkoutItems]);

  // ─── Initialize checkout items (aggregate by SKU) ───────────
  useEffect(() => {
    if (isOpen && items.length > 0) {
      // Aggregate items by SKU — the API may return the same SKU in multiple rows
      // (e.g., gift items). We sum quantities so barcode scanning works correctly.
      const skuMap = new Map<string, CheckoutItem>();
      for (const item of items) {
        const normalizedSku = normalizeCheckoutSku(item.sku);
        const existing = skuMap.get(normalizedSku);
        if (existing) {
          existing.quantity += item.quantity;
        } else {
          skuMap.set(normalizedSku, {
            ...item,
            sku: normalizedSku,
            quantity: item.quantity,
            verifiedCount: 0,
            isExempt: item.is_checkout_required === false,
          });
        }
      }
      const mapped = Array.from(skuMap.values());
      // Mark exempt items as fully verified
      mapped.forEach(item => {
        if (item.isExempt) {
          item.verifiedCount = item.quantity;
        }
      });
      setCheckoutItems(mapped);
      setScanInput('');
      setAlert(null);
      setPendingCameraScan(null);
      scanLockRef.current = false;
    }
  }, [isOpen, items]);

  // ─── Auto-focus input ──────────────────────────────────────
  useEffect(() => {
    if (isOpen && !cameraMode && !alert) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, cameraMode, alert]);

  // ─── Auto-focus alert OK button so user can press Enter to close ──
  useEffect(() => {
    if (!alert) return;
    const dismiss = () => {
      setAlert(null);
      inputRef.current?.focus();
    };
    // Move focus to the alert OK button as soon as it mounts
    const focusTimer = setTimeout(() => {
      const btn = document.getElementById('checkout-alert-ok-btn') as HTMLButtonElement | null;
      btn?.focus();
    }, 50);
    // Allow Enter/Escape to dismiss the alert from anywhere
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        dismiss();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [alert]);

  // ─── Barcode processing ────────────────────────────────────
  const processSku = useCallback((scannedSku: string) => {
    const sku = normalizeCheckoutSku(scannedSku);
    if (!sku) return;

    // Camera mode: block if scan lock is active (prevents duplicate reads)
    if (cameraModeRef.current && scanLockRef.current) return;

    const items = checkoutItemsRef.current;
    const itemIndex = items.findIndex(i => i.sku === sku);

    if (itemIndex === -1) {
      // Item not in order
      playError();
      setAlert({
        type: 'not-in-order',
        message: `O produto SKU "${sku}" não pertence a este pedido.`,
        sku,
      });
      return;
    }

    const item = items[itemIndex];

    if (item.isExempt) {
      // Already exempt, treat as success silently
      playBeep();
      return;
    }

    if (item.verifiedCount >= item.quantity) {
      // Quantity exceeded
      playError();
      setAlert({
        type: 'quantity-exceeded',
        message: `A quantidade do produto "${item.description || item.sku}" (SKU: ${item.sku}) já foi totalmente conferida. Quantidade esperada: ${item.quantity}.`,
        sku,
      });
      return;
    }

    // Success
    playBeep();

    if (cameraModeRef.current) {
      // Camera mode: show confirmation overlay before applying
      setPendingCameraScan({ sku, item: { ...item } });
      // Lock scanner to prevent duplicate reads while confirmation is pending
      scanLockRef.current = true;
      return;
    }

    // Keyboard/USB scanner mode: apply immediately
    setCheckoutItems(prev => {
      const idx = prev.findIndex(i => i.sku === sku);
      if (idx === -1) return prev;
      const updated = [...prev];
      updated[idx] = { ...prev[idx], verifiedCount: prev[idx].verifiedCount + 1 };
      return updated;
    });
  }, []);

  // ─── Camera scan confirmation ──────────────────────────────
  const handleConfirmCameraScan = useCallback(() => {
    if (!pendingCameraScan) return;
    const { sku } = pendingCameraScan;
    setPendingCameraScan(null);
    setCheckoutItems(prev => {
      const idx = prev.findIndex(i => i.sku === sku);
      if (idx === -1) return prev;
      const updated = [...prev];
      updated[idx] = { ...prev[idx], verifiedCount: prev[idx].verifiedCount + 1 };
      return updated;
    });
    // Keep lock active for 1.5s to prevent re-read of same barcode
    setTimeout(() => { scanLockRef.current = false; }, 1500);
  }, [pendingCameraScan]);

  const handleCancelCameraScan = useCallback(() => {
    setPendingCameraScan(null);
    // Short lock to prevent immediate re-trigger
    setTimeout(() => { scanLockRef.current = false; }, 800);
  }, []);

  // ─── Keyboard input handler (scanner detection) ────────────
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Block Ctrl+V / Cmd+V — fraud detection
    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
      e.preventDefault();
      playError();
      setAlert({
        type: 'fraud',
        message: 'Alerta de Fraude: Colar valores (Ctrl+V) não é permitido no processo de conferência. O código deve ser escaneado pelo leitor de código de barras.',
      });
      setScanInput('');
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const value = scanInput.trim();
      if (value.length >= SCANNER_MIN_LENGTH) {
        processSku(value);
      }
      setScanInput('');
      keystrokeTimestamps.current = [];
      scanBuffer.current = '';
      return;
    }
  }, [scanInput, processSku]);

  // Also intercept paste via the paste event
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    playError();
    setAlert({
      type: 'fraud',
      message: 'Alerta de Fraude: Colar valores não é permitido no processo de conferência. O código deve ser escaneado pelo leitor de código de barras.',
    });
    setScanInput('');
  }, []);

  // ─── Completion ────────────────────────────────────────────
  const allVerified = checkoutItems.length > 0 && checkoutItems.every(
    item => item.verifiedCount >= item.quantity,
  );

  const handleComplete = async () => {
    if (!allVerified) return;
    setCompleting(true);
    try {
      await onComplete();
    } finally {
      setCompleting(false);
    }
  };

  // ─── Stats ─────────────────────────────────────────────────
  const totalItems = checkoutItems.reduce((sum, i) => sum + i.quantity, 0);
  const verifiedItems = checkoutItems.reduce((sum, i) => sum + i.verifiedCount, 0);
  const pendingItemsList = checkoutItems.filter(i => i.verifiedCount < i.quantity);
  const verifiedItemsList = checkoutItems.filter(i => i.verifiedCount >= i.quantity);

  // ─── Render ────────────────────────────────────────────────
  if (!isOpen) return null;

  const alertBg = alert?.type === 'fraud'
    ? 'bg-red-600'
    : alert?.type === 'quantity-exceeded'
    ? 'bg-amber-500'
    : 'bg-red-500';

  const alertIcon = alert?.type === 'fraud'
    ? <ShieldAlert size={24} className="text-white flex-shrink-0" />
    : <AlertTriangle size={24} className="text-white flex-shrink-0" />;

  const modal = (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-60 z-40" />

      {/* Full-screen modal */}
      <div className="fixed inset-0 z-50 flex flex-col bg-white">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-slate-800 text-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <Package size={20} />
            <div>
              <h2 className="text-lg font-semibold leading-tight">Conferência do Pedido #{orderNumErp}</h2>
              <p className="text-sm text-slate-300">
                {verifiedItems}/{totalItems} itens conferidos
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Camera toggle (mobile) */}
            <button
              onClick={() => setCameraMode(!cameraMode)}
              className={`p-2 rounded-lg transition-colors ${cameraMode ? 'bg-emerald-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
              title={cameraMode ? 'Usar campo de texto' : 'Usar câmera'}
            >
              {cameraMode ? <Keyboard size={20} /> : <Camera size={20} />}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              title="Fechar"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-slate-200 flex-shrink-0">
          <div
            className="h-full bg-emerald-500 transition-all duration-300"
            style={{ width: `${totalItems > 0 ? (verifiedItems / totalItems) * 100 : 0}%` }}
          />
        </div>

        {/* Scanner input area */}
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex-shrink-0">
          {cameraMode ? (
            <div className="relative">
              <BarcodeScannerCamera
                active={cameraMode && isOpen}
                onScan={processSku}
              />
              {pendingCameraScan && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 rounded-lg">
                  <div className="bg-white rounded-xl shadow-2xl m-4 p-5 max-w-xs w-full text-center">
                    <CheckCircle2 size={40} className="text-emerald-500 mx-auto mb-2" />
                    <p className="text-sm font-bold text-slate-900 mb-1 truncate">
                      {pendingCameraScan.item.description || pendingCameraScan.sku}
                    </p>
                    <p className="text-xs text-slate-500 font-mono mb-1">SKU: {pendingCameraScan.sku}</p>
                    <p className="text-sm text-slate-600 mb-4">
                      {pendingCameraScan.item.verifiedCount + 1}/{pendingCameraScan.item.quantity} unidades
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleCancelCameraScan} className="flex-1">
                        Cancelar
                      </Button>
                      <Button variant="success" size="sm" onClick={handleConfirmCameraScan} className="flex-1">
                        Confirmar
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="max-w-lg mx-auto">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Escaneie o código de barras do produto
              </label>
              <input
                ref={inputRef}
                type="text"
                value={scanInput}
                onChange={e => setScanInput(normalizeCheckoutSku(e.target.value))}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                autoComplete="off"
                autoFocus
                placeholder="Aguardando leitura do código de barras..."
                className="w-full px-4 py-3 text-lg border-2 border-emerald-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-600 bg-white text-center font-mono"
              />
              <p className="text-xs text-slate-500 mt-1 text-center">
                O campo está pronto para receber a leitura do scanner USB
              </p>
            </div>
          )}
        </div>

        {/* Items list */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {/* Pending items */}
          {pendingItemsList.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <AlertTriangle size={14} className="text-amber-500" />
                Pendentes de conferência ({pendingItemsList.reduce((s, i) => s + (i.quantity - i.verifiedCount), 0)} restantes)
              </h3>
              <div className="space-y-2">
                {pendingItemsList.map(item => (
                  <div
                    key={item.sku}
                    className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg shadow-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{item.description || item.sku}</p>
                      <p className="text-xs text-slate-500 font-mono">SKU: {item.sku}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      <span className="text-sm font-medium text-slate-600">
                        {item.verifiedCount}/{item.quantity}
                      </span>
                      <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-400 transition-all duration-300"
                          style={{ width: `${(item.verifiedCount / item.quantity) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Verified items */}
          {verifiedItemsList.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-emerald-600 uppercase tracking-wider mb-3 flex items-center gap-2">
                <CheckCircle2 size={14} />
                Conferidos ({verifiedItemsList.length})
              </h3>
              <div className="space-y-2">
                {verifiedItemsList.map(item => (
                  <div
                    key={item.sku}
                    className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <CheckCircle2 size={18} className="text-emerald-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-emerald-800 truncate">{item.description || item.sku}</p>
                        <p className="text-xs text-emerald-600 font-mono">
                          SKU: {item.sku}
                          {item.isExempt && (
                            <span className="ml-2 text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                              Isento de conferência
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-emerald-700 ml-3 flex-shrink-0">
                      {item.quantity}/{item.quantity}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer with complete button */}
        <div className="px-4 py-3 bg-white border-t border-slate-200 flex-shrink-0">
          <div className="flex items-center justify-between max-w-lg mx-auto">
            <Button variant="outline" onClick={onClose} disabled={completing}>
              Cancelar
            </Button>
            <Button
              variant="success"
              icon={<CheckCircle2 size={18} />}
              onClick={handleComplete}
              disabled={!allVerified}
              loading={completing}
            >
              {allVerified ? 'Finalizar Conferência' : `Faltam ${totalItems - verifiedItems} itens`}
            </Button>
          </div>
        </div>
      </div>

      {/* Alert modal overlay */}
      {alert && createPortal(
        <>
          <div className="fixed inset-0 bg-black bg-opacity-40 z-[60]" onClick={() => { setAlert(null); inputRef.current?.focus(); }} />
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className={`w-full max-w-md rounded-xl shadow-2xl overflow-hidden animate-slide-up`}>
              <div className={`${alertBg} px-6 py-4 flex items-start gap-3`}>
                {alertIcon}
                <div className="flex-1">
                  <h3 className="text-white font-semibold text-lg">
                    {alert.type === 'fraud'
                      ? 'Alerta de Fraude'
                      : alert.type === 'quantity-exceeded'
                      ? 'Quantidade Excedida'
                      : 'Item Não Encontrado'}
                  </h3>
                </div>
              </div>
              <div className="bg-white px-6 py-5">
                <p className="text-slate-700 text-sm leading-relaxed">{alert.message}</p>
              </div>
              <div className="bg-slate-50 px-6 py-3 flex justify-end">
                <Button
                  id="checkout-alert-ok-btn"
                  variant={alert.type === 'fraud' ? 'danger' : 'primary'}
                  size="sm"
                  autoFocus
                  onClick={() => { setAlert(null); inputRef.current?.focus(); }}
                >
                  Entendi
                </Button>
              </div>
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );

  return createPortal(modal, document.body);
};
